
import { IOrganisation, Organisation } from "@/types";
import { useAdmin } from "@/services";
import { get } from "lodash-es";
import { defineComponent } from "vue";

export default defineComponent({
  name: "OrganisationEditor",
  props: {
    organisation: {
      type: Organisation,
      required: true,
    },
    organisationName: {
      type: String,
      required: true,
    },
    newOrgSetting: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update"],
  data: () => ({
    options: {
      mode: "code",
      theme: "ace/theme/chrome",
    },
    isLoading: false,
    refreshing: false,
  }),
  computed: {
    actionTitle(): string {
      return this.newOrgSetting ? "Create organisation" : "Update settings";
    },
    localData: {
      get(): IOrganisation {
        return { ...this.organisation.data };
      },
      set(data: IOrganisation) {
        this.$emit("update", data);
      },
    },
  },
  watch: {
    organisation: {
      handler() {
        this.refreshing = true;
        this.$nextTick(() => (this.refreshing = false));
      },
      immediate: true,
    },
    refreshing(val) {
      if (!val) {
        setTimeout(() => {
          const ace = get(this, ["$refs", "editor", "editor", "aceEditor"]);
          ace?.setOptions({
            fontSize: 15,
          });
        });
      }
    },
  },
  methods: {
    orgSettingsAction(): void {
      this.isLoading = true;
      this.newOrgSetting ? this.createOrgSetting() : this.updateOrgSetting();
    },
    getUpdatedProperties(res: any): string[] {
      const updatedProperties: string[] = [];
      Object.keys(res.data.updatedProperties).forEach((key) => {
        if (res.data.updatedProperties[key] === true) {
          updatedProperties.push(key);
        }
      });
      return updatedProperties;
    },
    createOrgSetting(): void {
      useAdmin()
        .createOrganisation({
          organisationId: this.organisationName,
          settings: this.organisation.settings || {},
        })
        .then(() =>
          this.makeToast(
            "success",
            `Created ${this.organisationName} organisation settings successfully.`
          )
        )
        .catch(() =>
          this.makeToast(
            "danger",
            `Failed to create ${this.organisationName} organisation settings/`
          )
        );
      this.isLoading = false;
    },
    updateOrgSetting(): void {
      useAdmin()
        .updateOrganisation({
          organisationId: this.organisationName,
          settings: this.organisation.settings || {},
        })
        .then((res: any) => {
          const updatedProperties = this.getUpdatedProperties(res);
          this.makeToast(
            "success",
            `Updated ${this.organisationName} Successfully`,
            updatedProperties
          );
          this.isLoading = false;
        })
        .catch(() =>
          this.makeToast("danger", `Failed to Update ${this.organisationName}`)
        );
    },
    makeToast(variant: string, message: string, sectionsUpdated?: string[]) {
      const content = sectionsUpdated?.length
        ? `Updated Sections: ${sectionsUpdated.join(", ")}`
        : "Organisation settings request";
      this.$bvToast.toast(content, {
        title: message,
        variant: variant,
        solid: true,
      });
    },
  },
});
